import MobileModalNavBar from '@/components/ui/MobileModalNavBar';
import { useMutateUnlinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateUnlinkSessionsToPatientProfile';
import { cn } from '@/lib/cn';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyP, TypographyPBold } from '@heidi/design-system/typography';
import { sanitizeError } from '@heidi/runtime/logger';
import { useQuerySessionById } from '@heidi/session/data';
import { atom, useAtom, useAtomValue, useStore } from 'jotai';
import { isMobile } from 'react-device-detect';
import { useIntl } from 'react-intl';

import {
  unlinkSessionBeforeConfirmAtom,
  unlinkSessionConfirmInFlightAtom,
} from './unlink-session-from-patient-modal.atoms';

export const UnlinkedSessionIdAtom = atom<string | undefined>(undefined);

const UnLinkSessionsFromPatientModal = () => {
  const intl = useIntl();
  const { mutateAsync: unlinkSessionsToPatientProfile } =
    useMutateUnlinkSessionsToPatientProfile();
  const [unlinkedSessionId, setUnlinkedSessionId] = useAtom(UnlinkedSessionIdAtom);
  const [beforeConfirm, setBeforeConfirm] = useAtom(unlinkSessionBeforeConfirmAtom);
  const { data: session } = useQuerySessionById({
    sessionId: unlinkedSessionId ?? '',
    select: (data) => {
      if (!data) return undefined;
      return {
        patient_profile: data.patient_profile,
        session_name: data.session_name,
      };
    },
  });
  const patientProfile = session?.patient_profile;

  const removeFromSessionTitle = intl.formatMessage({
    defaultMessage: 'Remove from session',
    id: 'DGWc3fA+/m',
    description:
      'Title of the confirm modal shown when removing a patient from the current session',
  });
  const removeConfirmMessage = intl.formatMessage(
    {
      defaultMessage:
        'Are you sure you want to remove this session from patient {patientName}?',
      id: 'bs7R1ebXDT',
      description:
        'Confirmation question shown before removing a patient from the current session',
    },
    { patientName: patientProfile?.demographic_string ?? '' }
  );
  const removePatientLabel = intl.formatMessage({
    defaultMessage: 'Remove patient',
    id: '/cSHPpwazA',
    description: 'Confirm button that removes the patient from the current session',
  });
  const cancelLabel = intl.formatMessage({
    defaultMessage: 'Cancel',
    id: 'hlSkBR2rzU',
    description: 'Dismisses the remove-patient confirmation without removing anything',
  });

  // The pre-confirm step and the unlink are one write, so a second confirm
  // while they run is ignored. Read from the store so a click in the same
  // event turn, or in another mounted instance, sees it before any
  // re-render. Cancel is disabled too, since it could not stop the write,
  // but Escape, a click outside and the mobile close still close: a stalled
  // request (offline, paused mutation) must not trap the user.
  const store = useStore();
  const isConfirming = useAtomValue(unlinkSessionConfirmInFlightAtom);

  const clearState = () => {
    setUnlinkedSessionId(undefined);
    setBeforeConfirm(null);
  };

  const onConfirmClick = async () => {
    if (store.get(unlinkSessionConfirmInFlightAtom)) {
      return;
    }
    store.set(unlinkSessionConfirmInFlightAtom, true);
    try {
      if (!patientProfile || !unlinkedSessionId) {
        return;
      }
      if (beforeConfirm?.sessionId === unlinkedSessionId) {
        await beforeConfirm.run();
      }
      await unlinkSessionsToPatientProfile({
        patientProfile,
        sessionIds: [unlinkedSessionId],
        sessionId: unlinkedSessionId,
      });
    } catch (error) {
      logger.error('remove_patient_confirm_failed', {
        sessionId: unlinkedSessionId,
        error: sanitizeError(error),
      });
    } finally {
      store.set(unlinkSessionConfirmInFlightAtom, false);
      // Closed mid-flight and reopened for another session: leave that one.
      // Reopened for this session: close it with this confirm's outcome.
      if (store.get(UnlinkedSessionIdAtom) === unlinkedSessionId) {
        clearState();
      }
    }
  };

  if (isMobile) {
    return (
      <DialogDrawer
        open={!!unlinkedSessionId}
        onOpenChange={(e) => {
          if (!e) {
            clearState();
          }
        }}
      >
        <DialogDrawerContent
          className={cn('w-[500px] gap-3 p-0 pb-6', isMobile && 'w-full')}
          hideCloseIcon
          dismissible={true}
        >
          <MobileModalNavBar onClose={clearState} />
          <div className="flex w-full flex-col gap-4 px-4 sm:pt-6">
            <DialogDrawerHeader className="flex justify-start p-0 pb-1.5">
              <DialogDrawerTitle>{removeFromSessionTitle}</DialogDrawerTitle>
            </DialogDrawerHeader>
            <TypographyP className={'text-text-secondary'}>
              {removeConfirmMessage}
            </TypographyP>

            <div
              className={cn(
                isMobile ? 'flex-col justify-center' : 'flex-row justify-start',
                'flex gap-2 pt-4'
              )}
            >
              <Button
                disableAutomaticSvgSizing
                variant="destructive"
                size="lg"
                disabled={isConfirming}
                onClick={() => void onConfirmClick()}
                className="flex-1 md:flex-none"
              >
                <TypographyPBold>{removePatientLabel}</TypographyPBold>
              </Button>
              <Button
                disableAutomaticSvgSizing
                variant={isMobile ? 'outline' : 'ghost'}
                className={cn(isMobile && 'border-0')}
                disabled={isConfirming}
                onClick={clearState}
                size="lg"
              >
                {cancelLabel}
              </Button>
            </div>
          </div>
        </DialogDrawerContent>
      </DialogDrawer>
    );
  }

  return (
    <DialogDrawer
      open={!!unlinkedSessionId}
      onOpenChange={(e) => {
        if (!e) {
          clearState();
        }
      }}
    >
      <DialogDrawerContent className="gap-4 p-6" hideCloseIcon dismissible={true}>
        <div className="flex w-full flex-col gap-2">
          <DialogDrawerHeader>
            <DialogDrawerTitle>{removeConfirmMessage}</DialogDrawerTitle>
          </DialogDrawerHeader>
        </div>

        <div className="flex flex-col justify-end gap-2 pt-4 md:flex-row">
          <Button
            disableAutomaticSvgSizing
            variant="ghost"
            disabled={isConfirming}
            onClick={clearState}
            size="lg"
          >
            {cancelLabel}
          </Button>
          <Button
            disableAutomaticSvgSizing
            variant="destructive"
            size="lg"
            disabled={isConfirming}
            onClick={() => void onConfirmClick()}
          >
            {removePatientLabel}
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default UnLinkSessionsFromPatientModal;
